import { useEffect } from "react"
import {
	areAdvancedFiltersAppliedDefault,
	ListView,
	Search,
	TSearchParams,
	useSearch,
} from "@sembark-travel/ui-list"
import { Link, useLocationQuery } from "@sembark-travel/ui-router"
import {
	AsyncSelect,
	AudioPlayer,
	Box,
	Button,
	Icons,
	Inline,
	joinAttributes,
	Select,
	Stack,
	Table,
	Text,
	Time,
	TimeDuration,
} from "@sembark-travel/ui-base"
import {
	DateRangePickerField,
	SelectField,
	SwitchInputField,
} from "@sembark-travel/ui-form"
import { IRole } from "../Roles"
import { useXHR } from "@sembark-travel/xhr"
import {
	dateToQuery,
	dateToUTCString,
	duration,
	endOf,
	isAfter,
	parseDateFromQuery,
	startOf,
	utcTimestampToLocalDateTimeString,
} from "@sembark-travel/datetime-utils"
import { Destinations, ITrip } from "../Trips"
import {
	CreateNewQueryFromRequestInDialog,
	ITripPlanRequest,
} from "../TripPlanRequests"
import { TIntegration } from "../Integrations"
import { IUser } from "../Users"
import { useNavigate } from "../router-utils"
import { PhoneNumber } from "../Contacts"
import { ITripSource } from "../TripSources"

type TFiltersInLocationQuery = TSearchParams & {
	agents?: Array<string>
	created_after?: string
	created_before?: string
	call_direction?: string
	subject_type?: string
	only_missed_calls?: "1" | "0"
}

function paramsToLocationQuery(
	params: TCallLogFilters
): TFiltersInLocationQuery {
	const query: TFiltersInLocationQuery = {}
	if (params.q) query.q = params.q
	if (params.page) query.page = params.page
	if (params.created_after)
		query.created_after = dateToQuery(params.created_after)
	if (params.created_before)
		query.created_before = dateToQuery(params.created_before)
	if (params.agents?.length) {
		query.agents = params.agents.map((a) => `${a.id}_${a.name}`)
	}
	if (params.call_direction) query.call_direction = params.call_direction.id
	if (params.subject_type) query.subject_type = params.subject_type.id
	if (params.only_missed_calls) query.only_missed_calls = "1"
	return query
}

function locationQueryToParams(
	query: TFiltersInLocationQuery
): TCallLogFilters {
	const params: TCallLogFilters = {}
	if (query.q) params.q = query.q
	if (query.page) params.page = query.page
	if (query.created_after)
		params.created_after = parseDateFromQuery(query.created_after)
	if (query.created_before)
		params.created_before = parseDateFromQuery(query.created_before)
	if (query.agents?.length) {
		params.agents = query.agents.map((s: string) => {
			const [id, ...name] = s.split("_")
			return { id: parseInt(id), name: name.join("_") }
		})
	}
	if (query.call_direction) {
		const directionOptions: Record<string, string> = {
			IBD: "In bound",
			OBD: "Out bound",
		}
		params.call_direction = {
			id: query.call_direction,
			name: directionOptions[query.call_direction] || query.call_direction,
		}
	}
	if (query.subject_type) {
		const subjectTypeOptions: Record<string, string> = {
			trips: "Trips",
			trip_plan_requests: "Trip Plan Requests",
		}
		params.subject_type = {
			id: query.subject_type,
			name: subjectTypeOptions[query.subject_type] || query.subject_type,
		}
	}
	if (query.only_missed_calls === "1") {
		params.only_missed_calls = 1
	}
	return params
}
export function CallLogsList() {
	const [query, setQuery] = useLocationQuery({
		fromQuery: locationQueryToParams,
		toQuery: paramsToLocationQuery,
	})
	const [params, setParams] = useSearch<TCallLogFilters>({
		...query,
	})
	useEffect(() => {
		setQuery(params)
	}, [params, setQuery])
	return (
		<Search<TCallLogFilters>
			initialParams={params}
			onSearch={(params) => {
				setParams({ ...params, page: 1 })
			}}
			title="Call Logs"
			Filters={Filters}
			areAdvancedFiltersApplied={(f) => {
				const { trip_sources, ...otherFilters } = f
				return areAdvancedFiltersAppliedDefault(otherFilters)
			}}
		>
			<CallLogsTable params={params} setParams={setParams} pagination={true} />
		</Search>
	)
}

function Filters() {
	return (
		<Stack gap="4">
			<SelectField select={SelectAgents} name="agents" label="Agent" multiple />
			<SelectField
				select={Select}
				label="Call Direction"
				name="call_direction"
				options={[
					{ id: "IBD", name: "In-bound" },
					{ id: "OBD", name: "Out-bound" },
				]}
			/>

			<DateRangePickerField
				label="Call Made At"
				fromName="created_after"
				toName="created_before"
				rangePresets={["Today", "Yesterday", "This Month", "Last Month"]}
			/>
			<SelectField
				select={Select}
				label="For"
				name="subject_type"
				options={[
					{ id: "trips", name: "Trips" },
					{ id: "trip_plan_requests", name: "Trip Plan Requests" },
				]}
			/>
			<SwitchInputField name="only_missed_calls" label="Missed calls only" />
		</Stack>
	)
}

type SelectAgentsProps = Omit<
	React.ComponentProps<typeof AsyncSelect>,
	"fetch"
> & {
	roles?: Array<IRole>
	searchOnlyByName?: 0 | 1
	excludeSelf?: 0 | 1
}

function SelectAgents({
	roles,
	searchOnlyByName,
	excludeSelf,
	...props
}: SelectAgentsProps) {
	const xhr = useXHR()
	return (
		<AsyncSelect
			multiple
			{...props}
			perFetchLimit={15}
			fetch={(q, { page }) =>
				xhr
					.get("/remote-call-agents", {
						params: {
							q,
							page,
							limit: 15,
							search_only_by_name: searchOnlyByName,
							exclude_self: excludeSelf,
							roles: roles?.length ? roles.map((r) => r.id) : undefined,
						},
					})
					.then((resp) => resp.data.data)
			}
			optionRenderer={({ option }) => (
				<Inline gap="1" justifyContent="between">
					<Text>{option.name}</Text>
					{option.deleted_at ? <Icons.Trash color={"danger"} /> : null}
				</Inline>
			)}
		/>
	)
}

export type TCallLogFilters = TSearchParams & {
	created_after?: string | Date
	created_before?: string | Date
	agents?: Array<{ id: number; name: string }>
	call_direction?: { id: string; name: string }
	subject_type?: { id: string; name: string }
	only_missed_calls?: 1 | 0 | boolean
	trip_sources?: Array<number>
}

export type TCallLog = {
	id: number
	remote_id: string
	customer_phone: string
	normalized_customer_phone: string
	customer_name: string
	call_direction: "IBD" | "OBD" | null
	remote_updated_at: string | null
	created_at: string
	updated_at: string | null
	total_call_duration: string | null
	call_recording_url: string | null
	trip?: ITrip
	trip_plan_request?: ITripPlanRequest
	is_missed_call?: boolean
	integrated_app: {
		created_at: string
		id: number
		integration: TIntegration
		updated_at: string
		updated_by?: IUser
		created_by?: IUser
	}
	agent: {
		id: number
		created_at: string
		name: string
		normalized_phone: string
		phone: string
		remote_id: string
		updated_at: string
		deleted_at: string
	} | null
	is_self?: boolean
	trip_source?: ITripSource
}

function filtersToRequestParams(filters: TCallLogFilters) {
	const {
		created_after,
		created_before,
		agents,
		call_direction,
		subject_type,
		only_missed_calls,
		...otherParams
	} = filters
	return {
		...otherParams,
		created_after: created_after
			? dateToUTCString(startOf(created_after, "day"))
			: undefined,
		created_before: created_before
			? dateToUTCString(endOf(created_before, "day"))
			: undefined,
		agents: agents?.length ? agents.map((a) => a.id) : undefined,
		call_direction: call_direction?.id || undefined,
		subject_type: subject_type?.id || undefined,
		only_missed_calls: only_missed_calls ? 1 : undefined,
	}
}

export function CallLogsTable({
	params,
	setParams,
	pagination = false,
	subjectType,
	subjectId,
}: {
	params: TCallLogFilters
	setParams: (params: TCallLogFilters) => void
	pagination?: boolean
	subjectType?:
		| "trips"
		| "trip_plan_requests"
		| "remote_call_logs"
		| "trip_sources"
	subjectId?: number
}) {
	const navigate = useNavigate()
	return (
		<ListView<TCallLog, TCallLogFilters>
			pageKey={`${subjectType}-${subjectId || ""}-call-logs`}
			params={params}
			onPageChange={
				pagination ? (page) => setParams({ ...params, page }) : () => undefined
			}
			fetch={async (xhr, params) => {
				const { subject_type, ...restParams } = filtersToRequestParams(params)
				const resp = await xhr.get(
					subjectType === "trip_sources"
						? `/trip-sources/${subjectId}/calls`
						: `/call-logs`,
					{
						params: {
							subject_id:
								subjectType === "trip_sources" ? undefined : subjectId,
							...restParams,
							subject_type:
								subjectType === "trip_sources"
									? undefined
									: subject_type || subjectType,
						},
					}
				)
				return resp.data
			}}
		>
			{({ items }) => (
				<Table
					bordered
					hover
					responsive
					alignCols={{ 1: "center" }}
					headers={["Guest", "Call Recording", "Agent", "For"]}
					rows={items.map((log, index) => {
						const hasName = Boolean(
							log.trip?.trip_source_contact?.name || log.trip?.tourist?.name
						)
						return [
							<Stack gap="1" key={index}>
								<Inline>
									<Text color="muted">
										{log.integrated_app.integration.name}
									</Text>
								</Inline>
								<Stack>
									<Inline alignItems="center" gap="2">
										<Box
											bgColor={
												log.call_direction === "IBD"
													? log.is_missed_call
														? "danger"
														: "inset"
													: log.is_missed_call
														? "warning"
														: "success"
											}
											rounded="md"
											paddingX="1"
										>
											{log.is_missed_call ? (
												log.call_direction === "IBD" ? (
													<Icons.MissedCall color="danger" />
												) : (
													<Icons.OutboundMissedCall
														size={"5"}
														color={"warning"}
													/>
												)
											) : log.call_direction === "IBD" ? (
												<Icons.IncomingCall color="muted" />
											) : (
												<Icons.OutgoingCall color="success" />
											)}
										</Box>
										<Inline alignItems="center" gap="1">
											<Stack>
												<Box fontWeight="semibold">
													{log.trip
														? log.trip.trip_source.is_agent
															? log.trip.trip_source_contact?.name
															: log.trip.tourist?.name
														: ""}
												</Box>
												<PhoneNumber
													fontWeight={hasName ? "normal" : "semibold"}
													fontSize={hasName ? "sm" : "base"}
													color={
														log.call_direction === "IBD"
															? log.is_missed_call
																? "danger"
																: "default"
															: log.is_missed_call
																? "warning"
																: "success"
													}
													value={log.normalized_customer_phone}
													numberOnly
												/>
											</Stack>
										</Inline>
										<PhoneNumber
											value={log.normalized_customer_phone}
											numberOnly
											contactName={log.customer_name}
											callLoggableId={
												log.trip
													? log.trip.id
													: log.trip_source
														? log.trip_source.id
														: log.trip_plan_request
															? log.trip_plan_request.id
															: log.id
											}
											callLoggableType={
												log.trip
													? "trips"
													: log.trip_source
														? "trip_sources"
														: log.trip_plan_request
															? "trip_plan_requests"
															: "remote_call_logs"
											}
											iconOnly
										/>
									</Inline>
									<Time
										fontSize={"sm"}
										color={"muted"}
										timestamp={log.created_at || ""}
										format="DD MMM YYYY, hh:mm A"
									/>
								</Stack>
							</Stack>,
							<Stack alignItems="center">
								{log.is_missed_call ? (
									log.call_direction === "IBD" ? (
										<Inline color={"danger"} alignItems="center">
											<Icons.MissedCall /> <i> Missed Call</i>
										</Inline>
									) : (
										<Inline color={"warning"} alignItems="center">
											<Icons.OutboundMissedCall size={"5"} />{" "}
											<i> Outbound - Missed Call</i>
										</Inline>
									)
								) : (
									<Inline alignSelf={"center"} justifyContent="center">
										{log.call_recording_url ? (
											<AudioPlayer src={log.call_recording_url} />
										) : (
											<Text color="muted">No recording</Text>
										)}
									</Inline>
								)}
							</Stack>,
							<Stack gap="1">
								<Inline gap="1" alignItems="center">
									<Text>{log.agent?.name}</Text>
									{log.agent?.deleted_at ? (
										<Icons.Trash
											title={utcTimestampToLocalDateTimeString(
												log.agent.deleted_at
											)}
											color="danger"
										/>
									) : null}
								</Inline>
								<Text fontSize={"sm"} color={"muted"}>
									{log.agent?.normalized_phone}
								</Text>
							</Stack>,
							log.trip ? (
								<Stack gap="px">
									<Link
										to={`/trips/${log.trip.id}`}
										anchored
										color="accent"
										fontWeight="semibold"
									>
										Trip {log.trip.id}
									</Link>
									<Destinations destinations={log.trip.destinations} />
									<Box color="muted" fontSize="sm">
										{joinAttributes(
											<Time value={log.trip.start_date} format="DD MMM" />,
											<TimeDuration value={duration(log.trip.days, "days")}>
												{log.trip.nights}N
											</TimeDuration>,
											<Text as="span">
												{log.trip.no_of_adults}A
												{log.trip.no_of_children
													? `, ${log.trip.no_of_children}C`
													: null}
											</Text>,
											isAfter(new Date(), log.trip.end_date) ? (
												<Text as="span" color="danger">
													<Icons.Info title="Past Dates" />
												</Text>
											) : null
										)}
									</Box>
								</Stack>
							) : log.trip_plan_request &&
							  !log.trip_plan_request?.deleted_at ? (
								<CreateNewQueryFromRequestInDialog
									request={log.trip_plan_request}
									onSuccess={(trip) => {
										navigate("/trips/:tripId", {
											params: { tripId: trip.id.toString() },
										})
									}}
								>
									{({ create }) => (
										<Inline gap="10" alignItems={"center"} minWidth="max">
											<Stack gap="1">
												<Text>Trip Plan Request</Text>
												<Text fontSize="sm" color="muted">
													Id: {log.trip_plan_request?.id}
												</Text>
											</Stack>
											<Button
												title="Create New Query"
												size="sm"
												onClick={create}
											>
												<Icons.Plus />
											</Button>
										</Inline>
									)}
								</CreateNewQueryFromRequestInDialog>
							) : log.trip_source ? (
								<Stack>
									<Link
										to={`/trip-sources/${log.trip_source.id}`}
										anchored
										color="accent"
										fontWeight="semibold"
									>
										Trip Source {log.trip_source.id}
									</Link>
									<Text>{log.trip_source.name}</Text>
								</Stack>
							) : null,
						]
					})}
				/>
			)}
		</ListView>
	)
}
